"use client"

import { useState, useEffect } from "react"
import { Plus, Edit2, Trash2, Check, X, MapPin, Sparkles } from "lucide-react"
import { createCity, getCities, updateCity, deleteCity as apiDeleteCity, getStates } from "../utils/api"

export default function City() {
  const [cities, setCities] = useState([])
  const [states, setStates] = useState([])
  const [loadingCities, setLoadingCities] = useState(true)
  const [loadingStates, setLoadingStates] = useState(true)
  const [error, setError] = useState(null)
  const [newCity, setNewCity] = useState("")
  const [selectedStateId, setSelectedStateId] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  // Track per-item actions
  const [savingId, setSavingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await getStates()
        const mapped = res.data.map((s) => ({ id: s._id || s.id, name: s.name }))
        setStates(mapped)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoadingStates(false)
      }
    }
    fetchStates()
  }, [])

  // Fetch cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await getCities()
        const mapped = res.data.map((c) => ({ id: c._id || c.id, name: c.name, stateId: c.state }))
        setCities(mapped)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoadingCities(false)
      }
    }
    fetchCities()
  }, [])

  const addCity = async () => {
    if (!newCity.trim() || !selectedStateId) return
    try {
      setSavingId("new") // Use a temporary ID for the new item being added
      const res = await createCity({ name: newCity.trim(), state: selectedStateId })
      setCities((prev) => [...prev, { id: res.data._id, name: res.data.name, stateId: res.data.state }])
      setNewCity("")
      setSelectedStateId("")
    } catch (err) {
      setError(err.message)
    } finally {
      setSavingId(null)
    }
  }

  const deleteCityHandler = async (id) => {
    try {
      setDeletingId(id)
      await apiDeleteCity(id)
      setCities((prev) => prev.filter((c) => c.id !== id))
    } catch (err) {
      setError(err.message)
    } finally {
      setDeletingId(null)
    }
  }

  const startEditing = (city) => {
    setEditingId(city.id)
    setEditingName(city.name)
  }

  const saveEdit = async () => {
    if (!editingName.trim()) return
    try {
      setSavingId(editingId)
      const res = await updateCity(editingId, { name: editingName.trim() })
      setCities((prev) => prev.map((c) => (c.id === editingId ? { ...c, name: res.data.name } : c)))
      cancelEdit()
    } catch (err) {
      setError(err.message)
    } finally {
      setSavingId(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const handleKeyPress = (e, action) => e.key === "Enter" && action()

  if (loadingStates || loadingCities) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-8 px-4">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl shadow-lg">
              <MapPin className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              City Management
            </h1>
          </div>
          <p className="text-gray-600 text-lg">Manage cities and their associated states</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5" />
              <strong className="font-bold">Error: </strong>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-sm font-medium underline hover:text-red-900">
              Dismiss
            </button>
          </div>
        )}

        {/* Add New City */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
            <div className="flex items-center gap-2">
              <Plus className="h-6 w-6" />
              <h2 className="text-xl font-semibold">Add New City</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label htmlFor="state-select" className="text-sm font-semibold text-gray-700">
                  Select State
                </label>
                <select
                  id="state-select"
                  value={selectedStateId}
                  onChange={(e) => setSelectedStateId(e.target.value)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white"
                  disabled={savingId === "new"}
                >
                  <option value="">-- Select State --</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="city-name" className="text-sm font-semibold text-gray-700">
                  City Name
                </label>
                <input
                  id="city-name"
                  type="text"
                  value={newCity}
                  placeholder="Enter city name..."
                  onChange={(e) => setNewCity(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addCity)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  disabled={savingId === "new"}
                />
              </div>
            </div>
            <button
              onClick={addCity}
              disabled={!newCity.trim() || !selectedStateId || savingId === "new"}
              className="mt-6 w-full h-12 px-8 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {savingId === "new" ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add City
                </>
              )}
            </button>
          </div>
        </div>

        {/* Cities List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Cities List</h2>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-full">
                <span className="font-semibold">
                  {cities.length} {cities.length === 1 ? "city" : "cities"}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {cities.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-12 h-12 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Cities Added Yet</h3>
                <p className="text-gray-600">Start by adding your first city above!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cities.map((city, index) => {
                  const state = states.find((s) => s.id === city.stateId)
                  return (
                    <div
                      key={city.id}
                      className="group bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                    >
                      {editingId === city.id ? (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                            <span className="text-orange-600 font-bold text-sm">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                            autoFocus
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            disabled={savingId === city.id}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={saveEdit}
                              disabled={!editingName.trim() || savingId === city.id}
                              className="h-10 px-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-medium disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              {savingId === city.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4" />
                                  Save
                                </>
                              )}
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={savingId === city.id}
                              className="h-10 px-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                              <span className="text-orange-600 font-bold text-sm">
                                {String(index + 1).padStart(2, "0")}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                                <MapPin className="w-3 h-3 text-white" />
                              </div>
                              <div>
                                <span className="font-semibold text-gray-900 text-lg">{city.name}</span>
                                <span className="block text-sm text-gray-500">{state?.name || "Unknown State"}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <button
                              onClick={() => startEditing(city)}
                              disabled={deletingId === city.id}
                              className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => deleteCityHandler(city.id)}
                              disabled={deletingId === city.id}
                              className="h-10 px-4 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              {deletingId === city.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}